// src/pages/moviment-detail/MovimentDetail.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../services/api";

import {
  ActionButton,
  ButtonPrintOrder,
  Container,
  InputSection,
  ScrollContainer,
  SectionButton,
  SectionUnion,
  SubTitle,
  Table,
  TableCell,
  TableRow,
  Title,
  TitleContainer,
  UnionCardSections
} from "./moviment-detail.styled";

import { FiTrash2 } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";

import {
  Autocomplete,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";

import ConfirmDialog from "../../components/ConfirmDialog";

import {
  DateTimePicker,
  LocalizationProvider,
  renderTimeViewClock
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { FaRegCheckCircle, FaRegFilePdf } from "react-icons/fa";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { ProductInterface, ToolsInterface } from "../../common/interfaces";
import CustomTooltip from "../../components/CustomTooltip";
import {
  ApprovalStatusLabel,
  TypeApprovalStatus
} from "../../components/enum/approvalStatusEnum";
import {
  MovimentStatusLabel,
  TypeMovimentStatus
} from "../../components/enum/movimentStatusEnum";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";

interface Moviment {
  work_order_id: number;
  moviment_reason: string;
  moviment_number: string;
  moviment_description: string;
  moviment_status: keyof typeof TypeMovimentStatus;
  tools: ToolsInterface[] | null;
  products: ProductInterface[] | null;
  user_id: number;
  client_id: number;
  start_date: string | null;
  end_date: string | null;
}

const MovimentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isUser, isOwner, isAdmin } = useAuth();

  const [moviment, setMoviment] = useState<Moviment | null>(null);
  const [tools, setTools] = useState<ToolsInterface[]>([]);
  const [products, setProducts] = useState<ProductInterface[]>([]);
  const [loading, setLoading] = useState(true);

  // modais e confirmações

  const [confirmRemoveTool, setConfirmRemoveTool] = useState<{
    open: boolean;
    toolId?: number;
  }>({ open: false });
  const [confirmRemoveProduct, setConfirmRemoveProduct] = useState<{
    open: boolean;
    productId?: number;
  }>({ open: false });

  // campos editáveis (sempre string)
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  // const [status, setStatus] = useState<keyof typeof TypeMovimentStatus>('CREATED');
  const [startDate, setStartDate] = useState(""); // vai armazenar ISO ou ''
  const [endDate, setEndDate] = useState("");

  const [initialDate, setInitialDate] = useState<Date | null>(null);
  const [finalDate, setFinalDate] = useState<Date | null>(null);

  const [iTools, setItools] = useState<ToolsInterface[]>([]);

  const [loadingTools, setLoadingTools] = useState(false);
  const [toolsOption, setToolsOption] = useState<ToolsInterface | null>(null);
  const [inputToolsValue, setInputToolsValue] = useState("");
  const [toolsSearch, setToolsSearch] = useState("");
  const [toolsQuantity, setToolsQuantity] = useState(0);
  const [toolsAddedActive, setToolsAddedActive] = useState(false);

  const [iProducts, setIproducts] = useState<ProductInterface[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsOption, setProductsOption] = useState<ProductInterface | null>(
    null
  );
  const [inputProductsValue, setInputProductsValue] = useState("");
  const [productsSearch, setProductsSearch] = useState("");
  const [productsQuantity, setProductsQuantity] = useState(0);
  const [productsAddedActive, setProductsAddedActive] = useState(false);

  const [approvalStatus, setApprovalStatus] =
    useState<keyof typeof TypeApprovalStatus>("PENDING");
  const [movimentStatus, setMovimentStatus] =
    useState<keyof typeof TypeMovimentStatus>("CREATED");

  const containerId = "toaster-moviment-detail";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const res = await api.get("/products", {
          params: {
            page: 1,
            limit: 8,
            product_name: productsSearch || undefined
          }
        });
        setIproducts(res.data.items || []);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [productsSearch]);

  useEffect(() => {
    const fetchTools = async () => {
      setLoadingTools(true);
      try {
        const res = await api.get("/tools", {
          params: {
            page: 1,
            limit: 8,
            search: toolsSearch || undefined
          }
        });
        setItools(res.data.items || []);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
      } finally {
        setLoadingTools(false);
      }
    };
    fetchTools();
  }, [toolsSearch]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get<any>(`/moviment/${id}`);

        setMoviment(data);
        setApprovalStatus(data.approval_status);
        setMovimentStatus(data.moviment_status);

        // FALLBACK: sempre array
        setTools(data.tools ?? []);
        setProducts(data.products ?? []);

        setReason(data.moviment_reason);
        setDescription(data.moviment_description);
        setMovimentStatus(data.moviment_status);

        // FALLBACK datas: se vier null, armazena ''
        setInitialDate(
          data.start_date ? new Date(data.start_date) : new Date()
        );
        setFinalDate(data.end_date ? new Date(data.end_date) : null);

        const db_products = data.productMoviment.map((p: any) => ({
          product_id: p.TB_PRODUCT.product_id,
          product_quantity: p.quantity,
          product_return_status: p.product_return_status,
          product_name: p.TB_PRODUCT.product_name
        })) as ProductInterface[];

        setProducts(db_products);

        const db_tools = data.toolsMoviment.map((t: any) => ({
          tools_id: t.TB_TOOLS.tools_id,
          tools_quantity: t.quantity,
          tools_return_status: t.tools_return_status,
          tools_name: t.TB_TOOLS.tools_name
        })) as ToolsInterface[];

        setTools(db_tools);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleRemoveTool = async () => {
    const toolId = confirmRemoveTool.toolId!;
    // 1) filtra antes
    const newTools = tools.filter((t) => t.tools_id !== toolId);
    // fecha o diálogo
    setConfirmRemoveTool({ open: false });

    try {
      // 2) atualiza localmente
      setTools(newTools);

      // 3) envia o PUT com o array já filtrado
      await api.put<Moviment>(`/moviment/${id}`, {
        ...moviment!,
        tools: newTools.map((t) => ({
          tools_id: t.tools_id,
          tools_quantity: t.tools_quantity,
          tools_return_status: t.tools_return_status
        })),
        products: products.map((p) => ({
          product_id: p.product_id,
          product_quantity: p.product_quantity,
          product_return_status: p.product_return_status
        })),
        moviment_reason: reason,
        moviment_description: description,
        moviment_status: movimentStatus,
        start_date: initialDate?.toISOString() || null,
        end_date: finalDate?.toISOString() || null
      });

      // 4) só agora mostre sucesso
      autoSuccess("Ativo removido com sucesso!");
    } catch (err) {
      console.error(err);
      handleHttpError(err);
    }
  };

  const handleRemoveProduct = async () => {
    const prodId = confirmRemoveProduct.productId!;
    const newProducts = products.filter((p) => p.product_id !== prodId);
    setConfirmRemoveProduct({ open: false });

    try {
      // 1) atualiza o state
      setProducts(newProducts);

      // 2) envia ao servidor usando o array já filtrado
      await api.put<Moviment>(`/moviment/${id}`, {
        ...moviment!,
        products: newProducts.map((p) => ({
          product_id: p.product_id,
          product_quantity: p.product_quantity,
          product_return_status: p.product_return_status
        })),
        tools: tools.map((t) => ({
          tools_id: t.tools_id,
          tools_quantity: t.tools_quantity,
          tools_return_status: t.tools_return_status
        })),
        moviment_reason: reason,
        moviment_description: description,
        moviment_status: movimentStatus,
        start_date: initialDate?.toISOString() || null,
        end_date: finalDate?.toISOString() || null
      });

      // 3) só agora confirma sucesso
      autoSuccess("Peça removida com sucesso!");
    } catch (err: any) {
      console.error(err);
      handleHttpError(err);
    }
  };

  const handleChangeStatusApproval = async (
    newStatus?: keyof typeof TypeApprovalStatus
  ) => {
    const payload = {
      status: newStatus ?? approvalStatus
    };

    try {
      await api.patch(`/moviment/${id}`, payload);
      autoSuccess("Status atualizado com sucesso!");
    } catch (err: any) {
      handleHttpError(err);
    }
  };

  const handleChangeStatus = async (
    newStatus?: keyof typeof TypeMovimentStatus
  ) => {
    const payload = {
      status: newStatus ?? movimentStatus
    };

    try {
      await api.patch(`/moviment/${id}/status`, payload);
      autoSuccess("Status atualizado com sucesso!");
    } catch (err: any) {
      handleHttpError(err);
    }
  };

  // Totais
  const totalTools = tools.reduce((acc, t) => acc + (t.tools_quantity || 0), 0);
  const totalProducts = products.reduce(
    (acc, p) => acc + (p.product_quantity || 0),
    0
  );

  // Save completo
  // 1. Mude a assinatura:
  const handleSaveMoviment = async () => {
    if (!moviment) return;

    // 2. Use o override ou, se não vier, o state
    const listTools = tools.map((t) => ({
      tools_id: t.tools_id,
      tools_quantity: t.tools_quantity,
      tools_return_status: t.tools_return_status
    }));
    const listProducts = products.map((p) => ({
      product_id: p.product_id,
      product_quantity: p.product_quantity,
      product_return_status: p.product_return_status
    }));

    const payload: Moviment = {
      ...moviment,
      moviment_reason: reason,
      moviment_description: description,
      moviment_status: movimentStatus,
      tools: listTools,
      products: listProducts,
      start_date: initialDate?.toISOString() || null,
      end_date: finalDate?.toISOString() || null
    };

    try {
      await api.put(`/moviment/${id}`, payload);
      autoSuccess("Moviment atualizado com sucesso!");
    } catch (err: any) {
      handleHttpError(err);
    }
  };

  if (loading) return <Container>Carregando...</Container>;
  if (!moviment) return <Container>Moviment não encontrado.</Container>;

  const clear = () => {
    setTools([]);
    setProducts([]);
    setReason("");
    setDescription("");
    setMovimentStatus("CREATED");
    setStartDate("");
    setEndDate("");
    setInitialDate(new Date());
    setFinalDate(null);
    // setToolsId('');
    setToolsOption(null);
    setInputToolsValue("");
    setToolsSearch("");
    setToolsQuantity(0);
    setToolsAddedActive(false);
  };

  const setToolsInList = async (tool: ToolsInterface) => {
    if (toolsQuantity <= 0) {
      autoError(
        "Para adicionar um ativo, a quantidade deve ser maior que zero."
      );
      return;
    }
    if (tools.some((t) => t.tools_id === tool.tools_id)) {
      return;
    }

    // 1) Atualiza localmente
    const updatedTools = [
      ...tools,
      {
        tools_id: tool.tools_id,
        tools_quantity: toolsQuantity,
        tools_return_status: 1,
        tools_name: tool.tools_name
      }
    ];
    setTools(updatedTools);

    // 2) Monta payload completo do movimento
    const payload: Moviment = {
      ...moviment!,
      moviment_reason: reason,
      moviment_description: description,
      moviment_status: movimentStatus,
      tools: updatedTools.map((t) => ({
        tools_id: t.tools_id,
        tools_quantity: t.tools_quantity,
        tools_return_status: t.tools_return_status
      })),
      products: products.map((p) => ({
        product_id: p.product_id,
        product_quantity: p.product_quantity,
        product_return_status: p.product_return_status
      })),
      start_date: initialDate?.toISOString() || null,
      end_date: finalDate?.toISOString() || null
    };

    try {
      // 3) Salva no servidor
      await api.put(`/moviment/${id}`, payload);
      autoSuccess("Ativo adicionado e movimento salvo com sucesso!");
    } catch (err: any) {
      console.error(err);
      // reverte em caso de erro
      setTools(tools);
      handleHttpError(err);
    } finally {
      setToolsAddedActive(false);
      setInputToolsValue("");
      setToolsSearch("");
      setToolsQuantity(0);
    }
  };

  const setProductsInList = async (product: ProductInterface) => {
    if (productsQuantity <= 0) {
      autoError(
        "Para adicionar um produto, a quantidade deve ser maior que zero."
      );
      return;
    }
    if (products.some((p) => p.product_id === product.product_id)) {
      return;
    }

    // 1) Atualiza localmente
    const updatedProducts = [
      ...products,
      {
        product_id: product.product_id,
        product_quantity: productsQuantity,
        product_return_status: 1,
        product_name: product.product_name
      }
    ];
    setProducts(updatedProducts);

    // 2) Monta payload completo do movimento
    const payload: Moviment = {
      ...moviment!,
      moviment_reason: reason,
      moviment_description: description,
      moviment_status: movimentStatus,
      tools: tools.map((t) => ({
        tools_id: t.tools_id,
        tools_quantity: t.tools_quantity,
        tools_return_status: t.tools_return_status
      })),
      products: updatedProducts.map((p) => ({
        product_id: p.product_id,
        product_quantity: p.product_quantity,
        product_return_status: p.product_return_status
      })),
      start_date: initialDate?.toISOString() || null,
      end_date: finalDate?.toISOString() || null
    };

    try {
      // 3) Salva no servidor
      await api.put(`/moviment/${id}`, payload);
      autoSuccess("Produto adicionado e movimento salvo com sucesso!");
    } catch (err: any) {
      console.error(err);
      // reverte em caso de erro
      setProducts(products);
      handleHttpError(err);
    } finally {
      setProductsAddedActive(false);
      setInputProductsValue("");
      setProductsSearch("");
      setProductsQuantity(0);
    }
  };

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <TitleContainer>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "45%",
            justifyContent: "space-between"
          }}
        >
          <Title>Moviment #{moviment.moviment_number.substring(4)}</Title>

          <FormControl sx={{ width: "200px" }} margin="normal">
            <InputLabel>Status da Movimentação</InputLabel>
            <Select
              disabled={isUser}
              value={movimentStatus}
              label="Status da Movimentação"
              onChange={(e) => {
                const newStatus = e.target
                  .value as keyof typeof TypeMovimentStatus;
                setMovimentStatus(newStatus);
                handleChangeStatus(newStatus);
              }}
            >
              {Object.values(TypeMovimentStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {MovimentStatusLabel[status]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            padding: "10px",
            borderRadius: "8px",
            width: "45%",
            gap: "12px",
            flexWrap: "wrap"
          }}
        >
          {!isUser && (
            <FormControl sx={{ width: "200px" }} margin="normal">
              <InputLabel>Status de Aprovação</InputLabel>
              <Select
                disabled={isUser}
                value={approvalStatus}
                label="Status de Aprovação"
                onChange={(e) => {
                  const newStatus = e.target
                    .value as keyof typeof TypeApprovalStatus;
                  setApprovalStatus(newStatus);
                  handleChangeStatusApproval(newStatus);
                }}
              >
                {Object.values(TypeApprovalStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {ApprovalStatusLabel[status]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* BOTÃO PDF */}

          <CustomTooltip title="Gerar PDF">
            <ButtonPrintOrder
              disabled={tools.length <= 0 && products.length <= 0}
              onClick={() => {
                api
                  .get(`/pdf/moviment/${id}/pdf`, { responseType: "blob" })
                  .then((r) => {
                    const url = URL.createObjectURL(
                      new Blob([r.data], { type: "application/pdf" })
                    );
                    window.open(url, "_blank");
                  });
              }}
            >
              <FaRegFilePdf
                color={
                  tools.length <= 0 && products.length <= 0 ? "grey" : "red"
                }
                size={35}
              />
            </ButtonPrintOrder>
          </CustomTooltip>

          {/* BOTÃO LIMPAR */}

          {/* BOTÃO SALVAR */}
          <CustomTooltip title="Salvar Movimentação">
            <Button variant="contained" onClick={handleSaveMoviment}>
              Salvar
            </Button>
          </CustomTooltip>
        </div>
      </TitleContainer>

      <InputSection>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <DateTimePicker
            label="Data da retirada"
            value={initialDate}
            onChange={setInitialDate}
            format="dd/MM/yyyy HH:mm"
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal"
              }
            }}
          />
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <DateTimePicker
            label="Data da devolução"
            value={finalDate}
            onChange={setFinalDate}
            format="dd/MM/yyyy HH:mm"
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal"
              }
            }}
          />
        </LocalizationProvider>
      </InputSection>

      <InputSection>
        <TextField
          fullWidth
          label="Motivo"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          margin="normal"
          multiline
          minRows={2}
          maxRows={2}
        />

        <TextField
          fullWidth
          label="Descrição"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          multiline
          minRows={2}
          maxRows={2}
        />
      </InputSection>

      <UnionCardSections>
        {/* Ativos */}
        <SectionUnion>
          <SectionButton>
            {!toolsAddedActive && <SubTitle>Ativos</SubTitle>}
            {!toolsAddedActive && (
              <CustomTooltip title="Buscar ativos">
                <ActionButton
                  onClick={() => setToolsAddedActive(true)}
                  title="Adicionar ativo"
                >
                  <IoMdAdd size={20} />
                </ActionButton>
              </CustomTooltip>
            )}

            {toolsAddedActive && (
              <Autocomplete
                sx={{ marginTop: "10px", width: "60%", marginLeft: 1 }}
                options={iTools}
                getOptionLabel={(opt) => opt.tools_name!.toString()}
                loading={loadingTools}
                // selected option
                value={toolsOption}
                onChange={(_, newVal) => {
                  setToolsOption(newVal);
                  // setToolsId(newVal ? newVal.tools_id : '');
                  // quando muda a opção, também refletimos no input
                  setInputToolsValue(newVal ? newVal.tools_id.toString() : "");
                }}
                // controla o texto que aparece dentro do campo
                inputValue={inputToolsValue}
                onInputChange={(_, newInput) => {
                  setInputToolsValue(newInput); // digitação / limpeza
                  setToolsSearch(newInput); // sua lógica de buscar clientes
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ativos"
                    InputLabelProps={{ shrink: !!inputToolsValue }} // força o label a flutuar
                    fullWidth
                  />
                )}
                disableClearable={false}
                fullWidth
              />
            )}

            {toolsAddedActive && (
              <TextField
                sx={{ width: "20%", marginLeft: 2, textAlign: "center" }}
                label="Qtde"
                type="number"
                value={toolsQuantity}
                onChange={(e) => setToolsQuantity(Number(e.target.value))}
                margin="normal"
              />
            )}

            {toolsAddedActive && (
              <CustomTooltip title="Adicionar Ativo">
                <ButtonPrintOrder
                  onClick={() => {
                    setToolsInList(toolsOption!);
                  }}
                >
                  <FaRegCheckCircle color="green" size={35} />
                </ButtonPrintOrder>
              </CustomTooltip>
            )}
          </SectionButton>
          <ScrollContainer>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Qtd</th>
                  <th>Nome</th>
                  <th>Status Retorno</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <TableRow key={t.tools_id}>
                    <TableCell>{t.tools_id}</TableCell>
                    <TableCell>{t.tools_quantity}</TableCell>
                    <TableCell>{t.tools_name}</TableCell>
                    <TableCell>{t.tools_return_status}</TableCell>
                    <TableCell>
                      <CustomTooltip title="Remover ativo">
                        <ActionButton
                          onClick={() =>
                            setConfirmRemoveTool({
                              open: true,
                              toolId: t.tools_id
                            })
                          }
                        >
                          <FiTrash2 size={18} color="#f44336" />
                        </ActionButton>
                      </CustomTooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </ScrollContainer>
        </SectionUnion>

        {/* Peças */}
        <SectionUnion>
          <SectionButton>
            {!productsAddedActive && <SubTitle>Produtos</SubTitle>}
            {!productsAddedActive && (
              <CustomTooltip title="Buscar produtos">
                <ActionButton
                  onClick={() => setProductsAddedActive(true)}
                  title="Adicionar peça"
                >
                  <IoMdAdd size={20} />
                </ActionButton>
              </CustomTooltip>
            )}

            {productsAddedActive && (
              <Autocomplete
                sx={{ marginTop: "10px", width: "60%", marginLeft: 1 }}
                options={iProducts}
                getOptionLabel={(opt) => opt.product_name!.toString()}
                loading={loadingProducts}
                // selected option
                value={productsOption}
                onChange={(_, newVal) => {
                  setProductsOption(newVal);

                  setInputProductsValue(
                    newVal ? newVal.product_id.toString() : ""
                  );
                }}
                // controla o texto que aparece dentro do campo
                inputValue={inputProductsValue}
                onInputChange={(_, newInput) => {
                  setInputProductsValue(newInput); // digitação / limpeza
                  setProductsSearch(newInput); // sua lógica de buscar clientes
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produtos"
                    InputLabelProps={{ shrink: !!inputProductsValue }} // força o label a flutuar
                    fullWidth
                  />
                )}
                disableClearable={false}
                fullWidth
              />
            )}

            {productsAddedActive && (
              <TextField
                sx={{ width: "20%", marginLeft: 2, textAlign: "center" }}
                label="Qtde"
                type="number"
                value={productsQuantity}
                onChange={(e) => setProductsQuantity(Number(e.target.value))}
                margin="normal"
              />
            )}

            {productsAddedActive && (
              <CustomTooltip title="Adicionar Produto">
                <ButtonPrintOrder
                  onClick={() => {
                    setProductsInList(productsOption!);
                  }}
                >
                  <FaRegCheckCircle color="green" size={35} />
                </ButtonPrintOrder>
              </CustomTooltip>
            )}
          </SectionButton>
          <ScrollContainer>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Qtd</th>
                  <th>Nome</th>
                  <th>Status Retorno</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <TableRow key={p.product_id}>
                    <TableCell>{p.product_id}</TableCell>
                    <TableCell>{p.product_quantity}</TableCell>
                    <TableCell>{p.product_name!}</TableCell>
                    <TableCell>{p.product_return_status}</TableCell>
                    <TableCell>
                      <CustomTooltip title="Remover produto">
                        <ActionButton
                          onClick={() =>
                            setConfirmRemoveProduct({
                              open: true,
                              productId: p.product_id
                            })
                          }
                        >
                          <FiTrash2 size={18} color="#f44336" />
                        </ActionButton>
                      </CustomTooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </ScrollContainer>
        </SectionUnion>
      </UnionCardSections>

      {/* Confirmações */}
      <ConfirmDialog
        open={confirmRemoveTool.open}
        title="Remover Ativo"
        description="Deseja remover este ativo?"
        onClose={() => setConfirmRemoveTool({ open: false })}
        onConfirm={handleRemoveTool}
      />
      <ConfirmDialog
        open={confirmRemoveProduct.open}
        title="Remover Peça"
        description="Deseja remover esta peça?"
        onClose={() => setConfirmRemoveProduct({ open: false })}
        onConfirm={handleRemoveProduct}
      />
    </Container>
  );
};

export default MovimentDetail;
